/**
 * Core Data Operations
 *
 * Platform-agnostic data service core with caching, auth, and company operations.
 * This module provides the foundation for data operations shared between web and mobile.
 *
 * @module @prosperia/shared/data/operations/core
 */

import {
  getSupabase,
  getCompanyId as sharedGetCompanyId,
  handleSupabaseError,
  createRealtimeSubscription,
  withCompanyFilter,
} from '../../api/supabase';
import { isWeb } from '../../adapters/platform';

/**
 * Core Data Service Class
 *
 * Provides caching infrastructure, subscription management, and core data operations.
 * Designed to be extended by platform-specific implementations.
 */
export class DataServiceCore {
  constructor() {
    this.subscriptions = new Map();
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // ============================================
  // Caching Helpers
  // ============================================

  /**
   * Get data from cache or fetch with rate limiting
   *
   * @param {string} cacheKey - Unique key for cached data
   * @param {Function} fetchFunction - Async function to fetch data if not cached
   * @returns {Promise<any>} Cached or fetched data
   */
  async getCachedOrRateLimited(cacheKey, fetchFunction) {
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const data = await fetchFunction();
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
    return data;
  }

  /**
   * Clear specific cache entry
   *
   * @param {string} cacheKey - Key to clear
   */
  clearCache(cacheKey) {
    this.cache.delete(cacheKey);
  }

  /**
   * Clear all cached data
   */
  clearAllCache() {
    this.cache.clear();
  }

  // ============================================
  // Company ID Resolution
  // ============================================

  /**
   * Get company ID for the current user
   *
   * @returns {Promise<string|null>} Company ID or null
   */
  async getCompanyId() {
    try {
      const supabase = getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) return null;
      return await sharedGetCompanyId(session.user);
    } catch (error) {
      console.error('[DataServiceCore] Error getting company ID:', error);
      return null;
    }
  }

  // ============================================
  // Authentication Methods
  // ============================================

  /**
   * Sign up a new user
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} userData - Additional user data
   * @param {Object} options - Platform-specific options
   * @param {string} options.redirectUrl - Email redirect URL (required for web)
   * @param {Function} options.onLegalAcceptance - Callback to record legal acceptance
   * @param {Function} options.onTrackSignUp - Callback for analytics tracking
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async signUp(email, password, userData = {}, options = {}) {
    try {
      console.log('[DataServiceCore] Starting signup process for:', email);
      const supabase = getSupabase();

      // Build sign up options
      const signUpOptions = {
        data: {
          ...userData,
          company_name: userData.company_name || 'My Company',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          phone: userData.phone || '',
          signup_source: userData.signup_source || 'website',
          pending_company_creation: true,
        },
      };

      // Add redirect URL if provided (typically from web)
      if (options.redirectUrl) {
        signUpOptions.emailRedirectTo = options.redirectUrl;
      }

      // 1. Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions,
      });

      if (authError) {
        console.error('[DataServiceCore] Auth signup error:', authError);
        throw authError;
      }

      console.log(
        '[DataServiceCore] User account created successfully:',
        authData.user?.id
      );

      // 2. Record legal document acceptance if provided
      if (authData.user?.id && userData.legalAcceptance && options.onLegalAcceptance) {
        try {
          const acceptanceResult = await options.onLegalAcceptance(authData.user.id, {
            ipAddress: userData.acceptanceIpAddress,
            userAgent: userData.acceptanceUserAgent,
          });

          if (!acceptanceResult?.success) {
            console.warn(
              '[DataServiceCore] Failed to record legal document acceptance:',
              acceptanceResult?.errors
            );
          } else {
            console.log('[DataServiceCore] Legal document acceptance recorded successfully');
          }
        } catch (acceptanceError) {
          console.warn('[DataServiceCore] Error recording legal acceptance:', acceptanceError);
        }
      }

      console.log('[DataServiceCore] Company will be created after email verification');

      // 3. Track sign up event if callback provided
      if (options.onTrackSignUp) {
        try {
          await options.onTrackSignUp({
            source: userData.signup_source || 'website',
            email: email,
          });
        } catch (trackError) {
          console.warn('[DataServiceCore] Failed to track sign up:', trackError);
        }
      }

      return { success: true, data: authData };
    } catch (error) {
      console.error('[DataServiceCore] Signup error:', error);
      handleSupabaseError(error, 'sign up');
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign in an existing user
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} options - Platform-specific options
   * @param {Function} options.onCompanyCreation - Callback to create company if needed
   * @returns {Promise<any>} Auth data
   */
  async signIn(email, password, options = {}) {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if user needs company creation after sign in
      if (data.user && data.user.user_metadata?.pending_company_creation) {
        if (options.onCompanyCreation) {
          await options.onCompanyCreation(data.user);
        } else {
          // Use default company creation
          await this.createCompanyForUser(data.user);
        }
      }

      return data;
    } catch (error) {
      handleSupabaseError(error, 'sign in');
    }
  }

  /**
   * Create company for user after authentication
   *
   * @param {Object} user - Supabase user object
   * @returns {Promise<string|null>} Company ID or null
   */
  async createCompanyForUser(user) {
    try {
      if (!user?.user_metadata?.pending_company_creation) {
        return null;
      }

      console.log('[DataServiceCore] Creating company for authenticated user:', user.id);
      const supabase = getSupabase();

      const companyName = user.user_metadata.company_name || 'My Company';
      const companyDomain = user.user_metadata.company_domain || user.email.split('@')[1];

      // Use the create_company_with_owner function
      const { data: companyId, error: companyError } = await supabase.rpc(
        'create_company_with_owner',
        {
          p_company_name: companyName,
          p_company_domain: companyDomain,
        }
      );

      if (companyError) {
        console.error('[DataServiceCore] Company creation error:', companyError);
        console.error('[DataServiceCore] FULL ERROR OBJECT:', JSON.stringify(companyError, null, 2));
        // Fallback to manual creation
        return await this.createCompanyManually(companyName, user.id);
      }

      console.log('[DataServiceCore] Company created successfully:', companyId);

      // Update user metadata to remove pending flag
      await supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          company_id: companyId,
          pending_company_creation: false,
        },
      });

      return companyId;
    } catch (error) {
      console.error('[DataServiceCore] Error creating company for user:', error);
      return null;
    }
  }

  /**
   * Fallback manual company creation
   *
   * @param {string} companyName - Name for the company
   * @param {string} userId - User ID to associate
   * @returns {Promise<string|null>} Company ID or null
   */
  async createCompanyManually(companyName, userId) {
    try {
      console.log('[DataServiceCore] Creating company manually for user:', userId);
      const supabase = getSupabase();

      // Create company record
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: companyName,
          created_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (companyError) {
        console.error('[DataServiceCore] Manual company creation error:', companyError);
        throw companyError;
      }

      // Create user access cache entry
      const { error: cacheError } = await supabase.from('user_access_cache').insert({
        user_id: userId,
        company_id: company.id,
        role_type: 'owner',
        is_owner: true,
        is_active: true,
        permissions: ['all'],
        created_at: new Date().toISOString(),
      });

      if (cacheError) {
        console.warn('[DataServiceCore] Failed to create user access cache:', cacheError);
      }

      // Update user metadata
      await supabase.auth.updateUser({
        data: {
          company_id: company.id,
          pending_company_creation: false,
        },
      });

      console.log('[DataServiceCore] Company created manually with ID:', company.id);
      return company.id;
    } catch (error) {
      console.error('[DataServiceCore] Error in manual company creation:', error);
      return null;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear all subscriptions
      this.clearAllSubscriptions();
      // Clear cache
      this.clearAllCache();
    } catch (error) {
      handleSupabaseError(error, 'sign out');
    }
  }

  /**
   * Get the current authenticated user
   *
   * @returns {Promise<Object|null>} User object or null
   */
  async getCurrentUser() {
    try {
      const supabase = getSupabase();
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) throw error;
      return session?.user || null;
    } catch (error) {
      handleSupabaseError(error, 'get current user');
    }
  }

  /**
   * Get the current auth session
   *
   * @returns {Promise<Object|null>} Session object or null
   */
  async getCurrentSession() {
    try {
      const supabase = getSupabase();
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      handleSupabaseError(error, 'get current session');
    }
  }

  // ============================================
  // Company Management
  // ============================================

  /**
   * Get the current user's company
   *
   * @returns {Promise<Object>} Company data
   */
  async getCompany() {
    try {
      const user = await this.getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const companyId = await sharedGetCompanyId(user);
      if (!companyId) throw new Error('Company ID not found');

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error, 'get company');
    }
  }

  /**
   * Update the current user's company
   *
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated company data
   */
  async updateCompany(updates) {
    try {
      const user = await this.getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const companyId = await sharedGetCompanyId(user);
      if (!companyId) throw new Error('Company ID not found');

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error, 'update company');
    }
  }

  // ============================================
  // Real-time Subscriptions
  // ============================================

  /**
   * Subscribe to table changes
   *
   * @param {string} table - Table name
   * @param {string} event - Event type ('*', 'INSERT', 'UPDATE', 'DELETE')
   * @param {Function} callback - Callback for changes
   * @param {Object} filter - Optional filter
   * @returns {Object} Subscription object
   */
  subscribeToTable(table, event, callback, filter = {}) {
    const subscriptionKey = `${table}_${event}`;

    // Remove existing subscription if it exists
    if (this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.get(subscriptionKey).unsubscribe();
    }

    // Create new subscription
    const subscription = createRealtimeSubscription(table, event, callback, filter);
    this.subscriptions.set(subscriptionKey, subscription);

    return subscription;
  }

  /**
   * Unsubscribe from table changes
   *
   * @param {string} table - Table name
   * @param {string} event - Event type
   */
  unsubscribeFromTable(table, event) {
    const subscriptionKey = `${table}_${event}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionKey);
    }
  }

  /**
   * Clear all active subscriptions
   */
  clearAllSubscriptions() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.clear();
  }
}

// Create singleton instance
const dataServiceCore = new DataServiceCore();

// Export class and singleton
export { dataServiceCore };
export default DataServiceCore;
